import { getCropRect, getDesignCanvas, getDocumentAspectRatio } from './reframe';

export const createExportPayload = (snapshot, settings = {}) => {
    const { layers, captions, aspectRatio, format, cropOffsets = {}, frame } = snapshot;
    const canvas = frame?.canvas || getDesignCanvas(aspectRatio);
    const payload = {
        outputFormat: settings.format || 'mp4',
        quality: settings.quality || 'high',
        duration: snapshot.duration ?? (Math.max(0, ...layers.flatMap(layer => layer.clips.map(clip => clip.startTime + clip.duration))) || 10),
        aspectRatio: format,
        canvas,
        cropRect: frame?.cropRect || getCropRect(canvas, format, cropOffsets[format]),
        exportCropOffsets: cropOffsets,
        layers: layers.map(layer => layer.id === 'captions-layer' ? {
            ...layer,
            appearance: captions?.appearance || {},
            styles: { fontFamily: 'Arial Black', ...(captions?.styles || captions?.style || {}) },
        } : layer),
    };
    // Make the request independent of later edits, renders, and progress updates.
    // These are the same JSON values the API receives, including remote source URLs.
    return JSON.parse(JSON.stringify(payload));
};

// Older original documents may omit durations. Resolve metadata only for those
// files, without opening that project in the UI or downloading whole files.
const readMediaDuration = (url, type) => new Promise(resolve => {
    const media = document.createElement(type === 'audio' ? 'audio' : 'video');
    const finish = () => {
        const duration = Number.isFinite(media.duration) && media.duration > 0 ? media.duration : 5;
        clearTimeout(timer);
        media.onloadedmetadata = null;
        media.onerror = null;
        media.removeAttribute('src');
        media.load();
        resolve(duration);
    };
    const timer = setTimeout(finish, 8000);
    media.onloadedmetadata = finish;
    media.onerror = finish;
    media.preload = 'metadata';
    media.src = url;
});

export const createSnapshotFromDocument = async (document, progress = {}, readDuration = readMediaDuration) => {
    if (!document?.timeline) throw new Error('The requested timeline was not found.');
    const groups = new Map();
    const durations = new Map();
    for (const [index, item] of (document.timeline.items || []).entries()) {
        const id = item.id || `layer-${index}`;
        if (!groups.has(id)) groups.set(id, []);
        groups.get(id).push(item);
    }
    const layers = await Promise.all(Array.from(groups, async ([id, items]) => {
        const first = items[0];
        const type = first.type.toLowerCase();
        const clips = await Promise.all(items.map(async (item, index) => {
            if (!item.file?.url && type !== 'text') return null;
            const startTime = item.trim?.start ?? item.startTime ?? 0;
            const trimStart = item.trim?.offset ?? item.trim?.start ?? 0;
            let duration = item.trim?.duration ?? item.duration ??
                (item.trim?.end !== undefined ? item.trim.end - startTime : undefined) ?? item.file?.duration;
            if (!(duration > 0) && ['video', 'audio'].includes(type)) {
                if (!durations.has(item.file.url)) durations.set(item.file.url, readDuration(item.file.url, type));
                duration = Math.max(0.1, await durations.get(item.file.url) - trimStart);
            }
            if (!(duration > 0)) duration = 5;
            return {
                id: `${id}-clip-${index}`, startTime, duration, trimStart, trimEnd: trimStart + duration,
                mediaFile: item.file ? { ...item.file, sourceUrl: item.file.url } : { type: 'text' },
                transform: { x: 0.5, y: 0.5, scale: 1, ...item.appearance },
                text: item.text, style: item.style, animation: item.animation,
                visible: item.visible !== false, muted: item.clipMuted || false,
            };
        }));
        return {
            id, type, name: first.layerName || first.category || id,
            zIndex: first.zIndex ?? 0, volume: first.volume ?? 1,
            visible: first.layerVisible ?? first.visible ?? true, muted: first.muted || false,
            sharedTransform: first.sharedTransform, clips: clips.filter(Boolean),
        };
    }));
    const data = document.captions;
    const captions = data ? {
        appearance: { x: 0.5, y: 0.8, scale: 1, maxWidth: 0.8, ...data.appearance },
        styles: data.styles || data.style || {},
    } : null;
    if (data?.content?.length) layers.push({
        id: 'captions-layer', type: 'captions', name: 'Captions', zIndex: 999,
        visible: data.visible !== false,
        clips: data.content.map((segment, index) => ({
            id: `caption-clip-${index}`, startTime: segment.startTime || 0, duration: segment.duration || 1,
            text: segment.text || (segment.words || []).map(word => word.content).join(' '), words: segment.words || [],
        })),
    });
    const aspectRatio = getDocumentAspectRatio(document);
    return {
        layers: layers.filter(layer => layer.clips.length).sort((a, b) => a.zIndex - b.zIndex),
        captions, aspectRatio, format: progress.format || progress.previewRatio || aspectRatio,
        cropOffsets: progress.exportCropOffsets || {},
    };
};
